import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';
import { 
  CreateDepensePartageeData, 
  CreateParticipationData 
} from '../../types/shared-expenses';

export const getDepensesPartagees = async () => {
  try {
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    let data: any = null; let error: any = null;
    if (impersonatedUserId) {
      const rpc = await supabase.rpc('admin_get_depenses_partagees', { p_user_id: impersonatedUserId });
      data = rpc.data; error = rpc.error;
    } else {
      const res = await supabase
        .from('depenses_partagees')
        .select(`
          *,
          participations (
            *,
            participant:participants (*)
          )
        `)
        .order('date', { ascending: false });
      data = res.data; error = res.error;
    }

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération des dépenses partagées:', error);
    return { data: null, error };
  }
};

export const getDepensesPartageesByMonthYear = async (mois: string, annee: string) => {
  try {
    const monthNum = parseInt(mois, 10);
    const yearNum = parseInt(annee, 10);
    const startDate = `${annee}-${mois.padStart(2, '0')}-01`;
    // Calculer le dernier jour du mois correctement
    const lastDay = new Date(yearNum, monthNum, 0).getDate();
    const endDate = `${annee}-${mois.padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    let data: any = null; let error: any = null;
    if (impersonatedUserId) {
      const rpc = await supabase.rpc('admin_get_depenses_partagees_by_month', { p_user_id: impersonatedUserId, p_mois: mois.padStart(2,'0'), p_annee: annee });
      data = rpc.data; error = rpc.error;
    } else {
      const res = await supabase
        .from('depenses_partagees')
        .select(`
          *,
          participations (
            *,
            participant:participants (*)
          )
        `)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: false });
      data = res.data; error = res.error;
    }

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération des dépenses partagées par mois:', error);
    return { data: null, error };
  }
};

export const createDepensePartagee = async (
  depense: CreateDepensePartageeData, 
  participations: CreateParticipationData[]
) => {
  try {
    // Récupérer l'utilisateur connecté
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      throw new Error('Utilisateur non connecté');
    }

    // Ajouter le user_id à la dépense
    const depenseWithUserId = {
      ...depense,
      user_id: user.id
    };

    // Commencer une transaction
    const { data: depenseData, error: depenseError } = await supabase
      .from('depenses_partagees')
      .insert([depenseWithUserId])
      .select()
      .single();

    if (depenseError) throw depenseError;

    if (depenseData && participations.length > 0) {
      // Ajouter les participations
      const participationsWithDepenseId = participations.map(p => ({
        ...p,
        depense_id: depenseData.id
      }));

      const { error: participationsError } = await supabase
        .from('participations')
        .insert(participationsWithDepenseId);

      if (participationsError) throw participationsError;
    }

    return { data: depenseData, error: null };
  } catch (error) {
    console.error('Erreur lors de la création de la dépense partagée:', error);
    return { data: null, error };
  }
};

// Fonction utilitaire pour valider et nettoyer les participations
const validateAndCleanParticipations = (participations: CreateParticipationData[], depenseId: string) => {
  if (!participations || participations.length === 0) {
    return [];
  }

  // S'assurer que toutes les participations ont les champs requis
  return participations.map(p => ({
    depense_id: depenseId,
    participant_id: p.participant_id,
    montant_paye: p.montant_paye || 0 // S'assurer que montant_paye n'est jamais null/undefined
  }));
};

export const updateDepensePartagee = async (
  id: string,
  depense: Partial<CreateDepensePartageeData>, 
  participations: CreateParticipationData[]
) => {
  try {
    // Mettre à jour la dépense
    const { data: depenseData, error: depenseError } = await supabase
      .from('depenses_partagees')
      .update(depense)
      .eq('id', id)
      .select()
      .single();

    if (depenseError) throw depenseError;

    if (depenseData && participations.length > 0) {
      // Valider et nettoyer les participations
      const cleanedParticipations = validateAndCleanParticipations(participations, id);
      
      // Supprimer les anciennes participations
      const { error: deleteError } = await supabase
        .from('participations')
        .delete()
        .eq('depense_id', id);

      if (deleteError) throw deleteError;

      // Ajouter les nouvelles participations validées
      const { error: participationsError } = await supabase
        .from('participations')
        .insert(cleanedParticipations);

      if (participationsError) throw participationsError;
    }

    return { data: depenseData, error: null };
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la dépense partagée:', error);
    return { data: null, error };
  }
};

export const deleteDepensePartagee = async (id: string) => {
  try {
    // Les participations seront supprimées automatiquement grâce à CASCADE
    const { error } = await supabase
      .from('depenses_partagees')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Erreur lors de la suppression de la dépense partagée:', error);
    return { error };
  }
};

export const getDepensePartageeById = async (id: string) => {
  try {
    const { data, error } = await supabase
      .from('depenses_partagees')
      .select(`
        *,
        participations (
          *,
          participant:participants (*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération de la dépense partagée:', error);
    return { data: null, error };
  }
};

// Fonction pour calculer les soldes entre participants pour un mois donné
export const calculateBalances = async (mois: string, annee: string) => {
  try {
    // Récupérer les dépenses partagées du mois
    const { data: depenses, error: depensesError } = await getDepensesPartageesByMonthYear(mois, annee);
    
    if (depensesError) throw depensesError;
    if (!depenses || depenses.length === 0) {
      return { data: { depenses: [], balances: [], totals: [] }, error: null };
    }

    // Récupérer toutes les participations pour ces dépenses
    const depenseIds = depenses.map((d: any) => d.id);
    
    // Vérification de la session
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { data: null, error: { message: 'Aucune session active' } };
    }

    // Récupérer les participations avec les détails des participants
    const { data: participations, error: participationsError } = await supabase
      .from('participations')
      .select(`
        *,
        participant:participants (
          id,
          nom
        )
      `)
      .in('depense_id', depenseIds);

    if (participationsError) throw participationsError;

    // Récupérer tous les participants
    const { data: participants, error: participantsError } = await supabase
      .from('participants')
      .select('*')
      .eq('user_id', user.id);

    if (participantsError) throw participantsError;

    // Calculer les totaux par participant
    const totals: Record<string, { paye: number, du: number, solde: number }> = {};
    
    // Initialiser les totaux pour tous les participants
    participants?.forEach((participant: any) => {
      totals[participant.id] = { paye: 0, du: 0, solde: 0 };
    });

    // Pour chaque dépense, calculer ce que chaque participant a payé et ce qu'il doit
    depenses.forEach((depense: any) => {
      const depenseParticipations = participations?.filter((p: any) => p.depense_id === depense.id) || [];
      const nbParticipants = depenseParticipations.length;
      
      if (nbParticipants === 0) return;
      
      const partIndividuelle = depense.montant_total / nbParticipants;
      
      depenseParticipations.forEach((participation: any) => {
        const participantId = participation.participant_id;
        
        // Ce que le participant a payé
        if (totals[participantId]) {
          totals[participantId].paye += participation.montant_paye || 0;
          
          // Ce que le participant doit (sa part)
          totals[participantId].du += partIndividuelle;
          
          // Solde = payé - dû
          totals[participantId].solde = totals[participantId].paye - totals[participantId].du;
        }
      });
    });

    // Calculer les balances entre participants
    const balances: { from: string, to: string, montant: number }[] = [];
    
    // Créer deux listes : débiteurs et créditeurs
    const debiteurs = Object.entries(totals)
      .filter(([_, values]) => values.solde < 0)
      .map(([id, values]) => ({ id, solde: values.solde }))
      .sort((a, b) => a.solde - b.solde); // Trier par solde croissant (les plus négatifs d'abord)
    
    const crediteurs = Object.entries(totals)
      .filter(([_, values]) => values.solde > 0)
      .map(([id, values]) => ({ id, solde: values.solde }))
      .sort((a, b) => b.solde - a.solde); // Trier par solde décroissant (les plus positifs d'abord)
    
    // Algorithme de règlement des dettes
    while (debiteurs.length > 0 && crediteurs.length > 0) {
      const debiteur = debiteurs[0];
      const crediteur = crediteurs[0];
      
      // Montant à transférer = minimum entre la dette et la créance (en valeur absolue)
      const montant = Math.min(Math.abs(debiteur.solde), crediteur.solde);
      
      if (montant > 0) {
        // Ajouter le transfert à la liste des balances
        balances.push({
          from: debiteur.id,
          to: crediteur.id,
          montant
        });
        
        // Mettre à jour les soldes
        debiteur.solde += montant;
        crediteur.solde -= montant;
      }
      
      // Retirer les participants dont le solde est réglé
      if (Math.abs(debiteur.solde) < 0.01) debiteurs.shift();
      if (Math.abs(crediteur.solde) < 0.01) crediteurs.shift();
    }

    // Enrichir les balances avec les noms des participants
    const enrichedBalances = balances.map(balance => {
      const fromParticipant = participants?.find((p: any) => p.id === balance.from);
      const toParticipant = participants?.find((p: any) => p.id === balance.to);
      
      return {
        ...balance,
        fromNom: fromParticipant?.nom || 'Inconnu',
        toNom: toParticipant?.nom || 'Inconnu'
      };
    });

    // Enrichir les dépenses avec les détails des participations
    const enrichedDepenses = depenses.map((depense: any) => {
      const depenseParticipations = participations?.filter((p: any) => p.depense_id === depense.id) || [];
      
      return {
        ...depense,
        participations: depenseParticipations
      };
    });

    // Enrichir les totaux avec les noms des participants
    const enrichedTotals = Object.entries(totals).map(([id, values]) => {
      const participant = participants?.find((p: any) => p.id === id);
      
      return {
        id,
        nom: participant?.nom || 'Inconnu',
        ...values
      };
    });

    return { 
      data: { 
        depenses: enrichedDepenses, 
        balances: enrichedBalances,
        totals: enrichedTotals
      }, 
      error: null 
    };
  } catch (error: any) {
    console.error('Erreur lors du calcul des soldes:', error);
    return { data: null, error };
  }
}; 